import httpRequest from "@/auth/http-client";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import ICategory, { ICategoryInput } from "@/interfaces/product.category.interfaces";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

export interface CategoryHierarchy {
  category: ICategory;
  children: CategoryHierarchy[];
  canCreateSubcategory: boolean;
  maxLevelReached: boolean;
}

export interface ValidateLevelResponse {
  canCreateSubcategory: boolean;
  currentLevel: number;
  nextLevel: number;
  maxLevelReached: boolean;
}

class CategoryService {
  private baseUrl = "api/v1/ProductCategory";

  async getAll(pagination: RequestPagination): Promise<PaginatedResponse<ICategory>> {
    const paginationQuery = buildPaginationQuery(pagination);

    const categories = await httpRequest.get(`${this.baseUrl}?${paginationQuery}`);
    return categories;
  }

  async getById(categoryId: number, accessToken?: string): Promise<ICategory> {
    const config = accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined;

    const category = await httpRequest.get(`${this.baseUrl}/${categoryId}`, config);

    return category;
  }

  async create(formData: ICategoryInput): Promise<void> {
    await httpRequest.post(this.baseUrl, formData);
  }

  async update(formData: ICategoryInput, categoryId: number): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${categoryId}`, formData);
  }

  async delete(categoryId: number): Promise<void> {
    await httpRequest.delete(`${this.baseUrl}/${categoryId}`);
  }

  async getByLevel(level: number): Promise<ICategory[]> {
    const categories = await httpRequest.get(`${this.baseUrl}/level/${level}`);
    return categories;
  }

  async getHierarchy(): Promise<CategoryHierarchy[]> {
    const hierarchy = await httpRequest.get(`${this.baseUrl}/hierarchy`);
    return hierarchy;
  }

  async validateLevel(parentId: number): Promise<ValidateLevelResponse> {
    const validation = await httpRequest.get(`${this.baseUrl}/validate-level/${parentId}`);
    return validation;
  }
}

const categoryService = new CategoryService();
export default categoryService;
